<template>
  <v-dialog
    v-model="dialogBusqueda"
    max-width="900"
    scrollable
    transition="dialog-top-transition">
    <template v-slot:activator="{ on, attrs }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on: tooltipOn }">
          <v-btn
            v-bind="attrs"
            v-on="{ ...on, ...tooltipOn }"
            depressed
            rounded
            small
            color="white"
            class="mr-2 busqueda-activador">
            <v-icon left small>mdi-magnify</v-icon>
            <span class="d-none d-md-inline text-caption">Buscar</span>
            <kbd class="ml-2 d-none d-md-inline">⌘K</kbd>
          </v-btn>
        </template>
        <span>Buscar (⌘K / Ctrl+K)</span>
      </v-tooltip>
    </template>

    <v-card class="busqueda-general-card" color="white">
      <!-- Header con campo de búsqueda -->
      <v-card-title class="busqueda-header pa-0">
        <v-text-field
          v-model="terminoBusqueda"
          ref="inputBusqueda"
          placeholder="Buscar obras, contratos, licitaciones..."
          prepend-inner-icon="mdi-magnify"
          filled
          rounded
          dense
          hide-details
          autofocus
          clearable
          class="busqueda-input"
          @keyup.esc="cerrarDialogo"
          @input="buscarConDebounce">
          <template v-slot:append>
            <v-chip
              v-if="cargando"
              small
              color="primary"
              text-color="white">
              <v-progress-circular
                indeterminate
                size="14"
                width="2"
                class="mr-1">
              </v-progress-circular>
              Buscando...
            </v-chip>
            <v-chip
              v-else-if="conteo && conteo.total > 0"
              small
              color="success"
              text-color="white">
              {{ conteo.total }} resultado(s)
            </v-chip>
          </template>
        </v-text-field>
      </v-card-title>

      <!-- Tabs para filtrar por tipo -->
      <v-tabs
        v-model="tabActivo"
        background-color="transparent"
        color="primary"
        grow
        show-arrows
        class="busqueda-tabs">
        <v-tab
          v-for="tab in tabs"
          :key="tab.valor"
          :disabled="getConteoTipo(tab.valor) === 0 && tab.valor !== null">
          <v-icon left small>{{ tab.icono }}</v-icon>
          {{ tab.texto }}
          <v-chip
            x-small
            class="ml-1"
            :color="getConteoTipo(tab.valor) > 0 ? 'primary' : 'grey lighten-1'">
            {{ getConteoTipo(tab.valor) }}
          </v-chip>
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <!-- Contenido de resultados -->
      <v-card-text class="busqueda-resultados pa-0">
        <!-- Estado: Sin búsqueda -->
        <div
          v-if="!terminoBusqueda || terminoBusqueda.length < 2"
          class="estado-vacio text-center py-10">
          <v-icon size="80" color="grey lighten-1">mdi-magnify</v-icon>
          <p class="text-h6 grey--text mt-4">
            Escribe al menos 2 caracteres para buscar
          </p>
          <p class="grey--text text--lighten-1">
            Busca por clave, número de contrato, meta, contratista...
          </p>
        </div>

        <!-- Estado: Cargando -->
        <div
          v-else-if="cargando"
          class="estado-cargando text-center py-10">
          <v-progress-circular
            indeterminate
            size="60"
            color="primary">
          </v-progress-circular>
          <p class="text-h6 grey--text mt-4">
            Buscando...
          </p>
        </div>

        <!-- Estado: Sin resultados -->
        <div
          v-else-if="resultadosFiltrados.length === 0"
          class="estado-vacio text-center py-10">
          <v-icon size="80" color="grey lighten-1">mdi-file-search-outline</v-icon>
          <p class="text-h6 grey--text mt-4">
            No se encontraron resultados
          </p>
          <p class="grey--text text--lighten-1">
            Intenta con otros términos de búsqueda
          </p>
        </div>

        <!-- Lista de resultados -->
        <v-list
          v-else
          two-line
          class="resultados-lista">
          <div
            v-for="(resultado, index) in resultadosFiltrados"
            :key="resultado.id + '-' + resultado.tipo">
            <!-- Resultado principal -->
            <v-list-item
              @click="irAResultado(resultado)"
              class="resultado-item">
              <v-list-item-avatar>
                <v-avatar
                  :color="getColorTipo(resultado.tipo)"
                  size="40">
                  <v-icon dark small>{{ getIconoTipo(resultado.tipo) }}</v-icon>
                </v-avatar>
              </v-list-item-avatar>

              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">
                  <v-chip
                    x-small
                    :color="getColorTipo(resultado.tipo)"
                    text-color="white"
                    class="mr-2">
                    {{ resultado.tipo }}
                  </v-chip>
                  {{ resultado.titulo }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="resultado.subtitulo">
                  {{ resultado.subtitulo | cortarTexto(100) }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action class="resultado-extra">
                <!-- Información extra según el tipo -->
                <div class="d-flex flex-column align-end">
                  <!-- Ejercicio -->
                  <v-chip
                    v-if="resultado.extra && resultado.extra.ejercicio"
                    x-small
                    outlined
                    class="mb-1">
                    {{ resultado.extra.ejercicio }}
                  </v-chip>

                  <!-- Importe -->
                  <span
                    v-if="resultado.extra && resultado.extra.importe"
                    class="text-caption font-weight-medium primary--text">
                    {{ resultado.extra.importe | moneda }}
                  </span>

                  <!-- Estatus -->
                  <v-chip
                    v-if="resultado.extra && resultado.extra.estatus"
                    x-small
                    color="info"
                    text-color="white"
                    class="mt-1">
                    {{ resultado.extra.estatus }}
                  </v-chip>

                  <!-- Fecha -->
                  <span
                    v-if="resultado.fecha"
                    class="text-caption grey--text mt-1">
                    {{ resultado.fecha | fechaCorta }}
                  </span>
                </div>
              </v-list-item-action>

              <v-list-item-action>
                <v-icon color="grey">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>

            <!-- Sub-items para Estimaciones y Reportes de contratos -->
            <template v-if="resultado.tipo === 'CONTRATO' && resultado.extra">
              <!-- Estimaciones -->
              <v-list-item
                v-if="resultado.extra.tieneEstimaciones"
                @click="irAEstimaciones(resultado.id)"
                class="resultado-subitem pl-10">
                <v-list-item-avatar size="32">
                  <v-avatar
                    color="#00897B"
                    size="32">
                    <v-icon dark x-small>mdi-calculator-variant</v-icon>
                  </v-avatar>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title class="text-body-2">
                    <v-chip
                      x-small
                      color="#00897B"
                      text-color="white"
                      class="mr-2">
                      ESTIMACIONES
                    </v-chip>
                    Ver {{ resultado.extra.cantidadEstimaciones }} estimación(es) del contrato
                  </v-list-item-title>
                </v-list-item-content>

                <v-list-item-action>
                  <v-icon small color="grey">mdi-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>

              <!-- Reportes -->
              <v-list-item
                v-if="resultado.extra.tieneReportes"
                @click="irAReportes(resultado.id)"
                class="resultado-subitem pl-10">
                <v-list-item-avatar size="32">
                  <v-avatar
                    color="#E65100"
                    size="32">
                    <v-icon dark x-small>mdi-clipboard-check-outline</v-icon>
                  </v-avatar>
                </v-list-item-avatar>

                <v-list-item-content>
                  <v-list-item-title class="text-body-2">
                    <v-chip
                      x-small
                      color="#E65100"
                      text-color="white"
                      class="mr-2">
                      REPORTES
                    </v-chip>
                    Ver {{ resultado.extra.cantidadReportes }} reporte(s) físico(s)
                  </v-list-item-title>
                </v-list-item-content>

                <v-list-item-action>
                  <v-icon small color="grey">mdi-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>
            </template>

            <v-divider
              v-if="index < resultadosFiltrados.length - 1"
              inset>
            </v-divider>
          </div>
        </v-list>
      </v-card-text>

      <!-- Footer -->
      <v-divider></v-divider>
      <v-card-actions class="busqueda-footer">
        <v-spacer></v-spacer>
        <span class="text-caption grey--text mr-4">
          <kbd>ESC</kbd> para cerrar
        </span>
        <v-btn
          text
          @click="cerrarDialogo">
          Cerrar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import BUSQUEDA_GENERAL from '@/graphql/querys/administracion/busquedaGeneral.gql';

export default {
  name: 'BusquedaGeneral',
  data() {
    return {
      dialogBusqueda: false,
      terminoBusqueda: '',
      tabActivo: 0,
      cargando: false,
      resultados: [],
      conteo: null,
      debounceTimer: null,
      tabs: [
        { texto: 'Todos', valor: null, icono: 'mdi-view-grid-outline' },
        { texto: 'Obras', valor: 'OBRA', icono: 'mdi-office-building' },
        { texto: 'Solicitudes', valor: 'SOLICITUD', icono: 'mdi-file-document-outline' },
        { texto: 'Licitaciones', valor: 'LICITACION', icono: 'mdi-gavel' },
        { texto: 'Contratos', valor: 'CONTRATO', icono: 'mdi-file-sign' },
      ],
    };
  },
  computed: {
    /**
     * Filtra resultados según el tab activo
     */
    resultadosFiltrados() {
      if (!this.resultados) return [];

      const tipoFiltro = this.tabs[this.tabActivo]?.valor;

      if (!tipoFiltro) {
        return this.resultados;
      }

      return this.resultados.filter((r) => r.tipo === tipoFiltro);
    },
  },
  watch: {
    dialogBusqueda(val) {
      if (val) {
        // Enfocar input cuando se abre el diálogo
        this.$nextTick(() => {
          if (this.$refs.inputBusqueda) {
            this.$refs.inputBusqueda.focus();
          }
        });
      } else {
        // Limpiar al cerrar
        this.limpiarBusqueda();
      }
    },
  },
  filters: {
    /**
     * Cortar texto largo
     */
    cortarTexto(texto, longitud) {
      if (!texto) return '';
      if (texto.length <= longitud) return texto;
      return `${texto.substring(0, longitud)}...`;
    },
    /**
     * Formatear moneda
     */
    moneda(valor) {
      if (!valor) return '$0.00';
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
      }).format(valor);
    },
    /**
     * Formatear fecha corta
     */
    fechaCorta(fecha) {
      if (!fecha) return '';
      const date = new Date(fecha);
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
  },
  methods: {
    /**
     * Buscar con debounce para evitar muchas peticiones
     */
    buscarConDebounce() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.debounceTimer = setTimeout(() => {
        this.ejecutarBusqueda();
      }, 400);
    },

    /**
     * Ejecutar búsqueda en el backend
     */
    async ejecutarBusqueda() {
      if (!this.terminoBusqueda || this.terminoBusqueda.length < 2) {
        this.resultados = [];
        this.conteo = null;
        return;
      }

      this.cargando = true;

      try {
        const { data } = await this.$apollo.query({
          query: BUSQUEDA_GENERAL,
          variables: {
            termino: this.terminoBusqueda,
            limite: 20,
            tiposFiltro: null, // Buscar en todos los tipos
          },
          fetchPolicy: 'network-only',
        });

        if (data && data.busquedaGeneral) {
          this.resultados = data.busquedaGeneral.resultados;
          this.conteo = data.busquedaGeneral.conteo;
        }
      } catch (error) {
        console.error('Error en búsqueda general:', error);
        this.$mensaje.error('Error al realizar la búsqueda');
      } finally {
        this.cargando = false;
      }
    },

    /**
     * Obtener conteo por tipo para los tabs
     */
    getConteoTipo(tipo) {
      if (!this.conteo) return 0;

      if (tipo === null) return this.conteo.total;

      const mapeoTipos = {
        OBRA: 'obras',
        SOLICITUD: 'solicitudes',
        LICITACION: 'licitaciones',
        CONTRATO: 'contratos',
      };

      return this.conteo[mapeoTipos[tipo]] || 0;
    },

    /**
     * Obtener color según el tipo
     */
    getColorTipo(tipo) {
      const colores = {
        OBRA: '#4CAF50',
        SOLICITUD: '#2196F3',
        LICITACION: '#FF9800',
        CONTRATO: '#3F51B5',
      };
      return colores[tipo] || '#757575';
    },

    /**
     * Obtener icono según el tipo
     */
    getIconoTipo(tipo) {
      const iconos = {
        OBRA: 'mdi-office-building',
        SOLICITUD: 'mdi-file-document-outline',
        LICITACION: 'mdi-gavel',
        CONTRATO: 'mdi-file-sign',
      };
      return iconos[tipo] || 'mdi-file-outline';
    },

    /**
     * Navegar al resultado seleccionado
     */
    irAResultado(resultado) {
      const rutas = {
        OBRA: { name: 'obra', params: { idObra: resultado.id } },
        SOLICITUD: { name: 'solicitudesObras' },
        LICITACION: { name: 'licitacion', params: { idLicitacion: resultado.id } },
        CONTRATO: { name: 'contrato', params: { idContrato: resultado.id } },
      };

      const ruta = rutas[resultado.tipo];

      if (ruta) {
        this.cerrarDialogo();
        this.$router.push(ruta).catch(() => {
          // Ignorar error de navegación duplicada
        });
      }
    },

    /**
     * Navegar a las estimaciones de un contrato
     */
    irAEstimaciones(idContrato) {
      this.cerrarDialogo();
      this.$router.push({
        name: 'contratoEstimaciones',
        params: { idContrato },
      }).catch(() => {
        // Ignorar error de navegación duplicada
      });
    },

    /**
     * Navegar a los reportes físicos de un contrato
     */
    irAReportes(idContrato) {
      this.cerrarDialogo();
      this.$router.push({
        name: 'contratoEjecucionReportes',
        params: { idContrato },
      }).catch(() => {
        // Ignorar error de navegación duplicada
      });
    },

    /**
     * Cerrar diálogo
     */
    cerrarDialogo() {
      this.dialogBusqueda = false;
    },

    /**
     * Limpiar búsqueda
     */
    limpiarBusqueda() {
      this.terminoBusqueda = '';
      this.resultados = [];
      this.conteo = null;
      this.tabActivo = 0;
    },

    /**
     * Manejar atajos de teclado
     */
    handleKeydown(e) {
      // Ctrl+K o Cmd+K para abrir búsqueda
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        this.abrirBusqueda();
      }
    },

    /**
     * Abrir búsqueda (método público para llamar desde fuera)
     */
    abrirBusqueda() {
      this.dialogBusqueda = true;
    },
  },
  mounted() {
    // Atajo Ctrl+K o Cmd+K para abrir búsqueda
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
};
</script>

<style lang="scss" scoped>
.busqueda-general-card {
  border-radius: 16px !important;
  overflow: hidden;

  .busqueda-header {
    background: linear-gradient(135deg, #05053e 0%, #1B1BAF 50%, #7373e0 100%);
    padding: 16px !important;

    .busqueda-input {
      ::v-deep .v-input__slot {
        background: white !important;
      }
    }
  }

  .busqueda-tabs {
    ::v-deep .v-tab {
      text-transform: none;
      font-weight: 500;
      letter-spacing: 0;
    }
  }

  .busqueda-resultados {
    max-height: 450px;
    overflow-y: auto;

    .estado-vacio,
    .estado-cargando {
      min-height: 200px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .resultados-lista {
      .resultado-item {
        cursor: pointer;
        transition: background-color 0.2s ease;

        &:hover {
          background-color: rgba(0, 0, 0, 0.04);
        }
      }

      .resultado-extra {
        min-width: 120px;
        align-items: flex-end;
      }

      .resultado-subitem {
        background-color: #fafafa;
        border-left: 3px solid #e0e0e0;
        min-height: 48px;
        cursor: pointer;
        transition: background-color 0.2s ease, border-color 0.2s ease;

        &:hover {
          background-color: #f0f0f0;
          border-left-color: #1B1BAF;
        }
      }
    }
  }

  .busqueda-footer {
    background-color: #f5f5f5;

    kbd {
      background-color: #e0e0e0;
      border-radius: 4px;
      padding: 2px 6px;
      font-size: 11px;
      font-family: monospace;
    }
  }
}

.busqueda-activador {
  kbd {
    background-color: rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 10px;
    font-family: system-ui, -apple-system, sans-serif;
    color: #666;
  }
}
</style>
